/**
 * 学生端 · 学校作业（`/student/homework`）。
 *
 * ## 这一页解决什么
 *
 * 家长在家长端录入"今天有哪些科目的哪些作业"，孩子在这里逐条做：
 *
 *   按**科目**分组 → 每一项点「开始」→ 填预计时长 → 计时（暂停 / 继续）
 *     → 点「完成」→ **进入「待家长验收」**
 *
 * ## 四条产品要求（都是这一页的存在理由，写错了就是 bug）
 *
 * 1. **每科下面的作业都完成了，这一科才算完成**。判定在服务端
 *    （`subjectCompletion()`：全部终结 **且至少 1 项 done**），这里只如实显示
 *    「已完成 2/3」与「✅ 这一科完成」，**不在前端重算** ——
 *    重算就等于把同一条规则变成两份，迟早漂移。
 * 2. **待验收必须显眼**：孩子点完成只到 `submitted`（**不计分**）。
 *    界面上那一项要明确写「待家长验收」，而不是「已完成」——
 *    否则孩子会以为分已经拿到手了（这是本轮明确要求的一条）。
 * 3. **「超时」是与「今天的作业」分开的一个标签**（用户明确要求分开）。
 *    数据来自 `GET /students/:id/homework/overdue`，排序（`dueAt` 倒序，
 *    最新截止的在最上面）由**服务端**保证，前端不重排。
 *    ⚠️ 超时项**仍然可以开始 / 完成** —— 这正是"没完成的会一直留着，
 *    直到完成或家长点放弃"的实现方式：「超时」是派生视图，不是状态。
 *
 *    2026-10-17 起这一页是**三个**标签：「今天的作业」/「**还有效**」/「超时」。
 *    中间那个补的是用户实测报的 bug：周五布置、周日截止的作业，周六在
 *    「今天的作业」（按**布置日** `checkin.due_date` 筛）和「超时」
 *    （按**截止时刻** `checkin.due_at < now()` 筛）里**都看不见**，
 *    孩子连「完成」按钮都点不到。「还有效」的判据是它们的**严格补集**：
 *    `due_at >= now()` 且 `due_date < 今天`（去重：今天布置的归第一栏）。
 * 4. **学生不能放弃作业项**：界面**不给**"放弃"按钮（后端会返回
 *    `HOMEWORK_SKIP_PARENT_ONLY`，但我们不靠"点了会失败"来保护）。
 *    这一页因此**完全不渲染 skip 的入口**，与今日打卡页刻意不同 ——
 *    那里是普通打卡，"今天这次不做了"是孩子的既有权利。
 *
 * ## 计时：**复用**，一个字都没有另写
 *
 * 作业项 = `once` 打卡 + `need_approval=true`，所以它走的就是既有的
 * `/checkins/:id/start | pause | resume | finish`：
 *  - 显示用 `useServerTimer`（服务端锚点 + 单调时钟，改手机时间无效）；
 *  - 写操作用 `useCheckinTimerMutations`（幂等键、心跳自动暂停都在里面）；
 *  - 时长一律取服务端的 `actualSeconds`（作业接口在 running 时会用
 *    `recompute_checkin_seconds()` 实时重算，与 `/student/today` 同一口径）。
 *
 * ## 手机优先
 *
 * 科目是卡片、作业项是卡片内的行，纵向排布；文案行用 `min-w-0 flex-1` +
 * `shrink-0`，窄屏不会挤成"一个字一行"。操作按钮用 `flex-wrap`，
 * 儿童模式下走 `size="kid"`（≥56px 点击区、≥20px 字号）。
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { Badge, Card, EmptyState, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { OverdueRow } from '../../components/homework/OverdueRow';
import { CheckinTimerPanel } from '../../components/ui/CheckinTimerPanel';
import { useToast } from '../../components/ui/Toast';
import { ErrorState, iconForName, statusMeta } from '../../components/ui/Bits';
import {
  useHomeworkCheckinMutations,
  useHomeworkDay,
  useHomeworkOpen,
  useHomeworkOverdue,
  usePointsSummary,
} from '../../lib/domainQueries';
import { useMyStudent } from '../../lib/useMyStudent';
import { useAuth } from '../../lib/auth';
import { isChildMode, resolveUiMode, uiModeHint } from '../../lib/uiMode';
import { newIdempotencyKey } from '../../lib/api/client';
import { toErrorMessage } from '../../lib/api/error';
import { primeEstimateChimeAudio } from '../../lib/estimateChimeAudio';
import { cn, formatPlannedDuration } from '../../lib/utils';
import {
  checkFinishAmount,
  finishAmountHint,
  finishAmountLabel,
} from '../../lib/finishAmount';
import {
  isHomeworkItemEmpty,
  type HomeworkCheckinView,
  type HomeworkItemView,
} from '../../lib/api/homework';
// 「实际用时」的显示口径（唯一出处，见 shared/duration.ts）与「超时多久」的
// 判据 + 文案（shared/homework-overdue.ts，家长端用同一份）
import {
  dedupeHomeworkQueueEntries,
  describeStudentHomeworkDueText,
  defaultStartEstimate,
  formatDuration,
  formatOverdueDuration,
  homeworkOverdueSeconds,
  startEstimatePresets,
  type StartEstimate,
} from '@fht/shared';

/** 预计时长快选（分钟）。与今日打卡页保持同一组值，孩子的肌肉记忆一致。 */
const DURATION_PRESETS = [10, 15, 20, 30, 45, 60] as const;

/**
 * 三个标签。
 *
 * - `today`：「今天的作业」（按**布置日** = 今天筛，见 `GET .../homework?date=`）；
 * - `open`：「**还有效**」—— 还没到期、但**不是今天布置**的未完成项
 *   （2026-10-17 新增，补的就是用户报的那条缝：周五布置、周日截止的作业
 *   周六在 `today` 与 `overdue` 里都看不见，孩子连「完成」都点不到）；
 * - `overdue`：「超时」—— 已经过了截止时刻的未完成项（**跨天保留**的派生视图）。
 *
 * ⚠️ `open` 与 `overdue` 在判据上**严格互补**（`due_at >= now` vs `< now`），
 * 所以同一条作业绝不会在两个标签里各出现一次。
 * ⚠️ `open` 与 `today` 也**不会重复**：服务端给「还有效」的查询带
 * `due_date < 今天`，而 `today` 那一支读的是 `due_date = 今天` 的作业单。
 */
type HomeworkTab = 'today' | 'open' | 'overdue';

/**
 * 这条作业项还可不可以点（有实例 = 可操作对象；学生本人/家长有打卡权）。
 *
 * ⚠️ 这里**曾经**有一个 `toCheckin()` 把作业项映射成计时 hook 的 `Checkin` 形状。
 * 现在计时面板（`CheckinTimerPanel`）自己接收 `checkinId / status / actualSeconds`
 * 这三个字段，所以那一层映射被删掉了 —— 「不造字段」这条约束因此变成**结构**上的：
 * 面板不需要 `startedAt`（本地时间不可信，见 `useServerTimer` 的注释）。
 */
function canActOn(item: HomeworkItemView): boolean {
  return item.canCheckin && item.active && item.checkin !== null;
}

// ================================================================ 一条作业项

interface ItemRowProps {
  item: HomeworkItemView;
  kid: boolean;
  busy: boolean;
  /**
   * 这一项的打卡记录（`null` = 还没有可操作记录）。
   *
   * ⚠️ 刻意传**整个 checkin** 而不是算好的秒数：要不要显示「已超时 X」是
   * `@fht/shared` 的唯一判据（`homeworkOverdueSeconds()` = 秒数 > 0 **且**状态
   * 未终结），在调用处先算一遍就等于把这条判据变成两份 —— 而"已完成/已放弃
   * 还挂着已超时"正是这么来的（缓存里的秒数是请求那一刻的静态值）。
   */
  checkin: HomeworkCheckinView | null;
  onStart: (item: HomeworkItemView) => void;
  onPause: (item: HomeworkItemView) => void;
  onResume: (item: HomeworkItemView) => void;
  onFinish: (item: HomeworkItemView) => void;
}

function HomeworkItemRow({
  item,
  kid,
  busy,
  checkin,
  onStart,
  onPause,
  onResume,
  onFinish,
}: ItemRowProps) {
  const meta = statusMeta(item.status);
  // 计时：只有 running 才推进；秒数来自服务端锚点（见 useServerTimer 的注释）
  // ⚠️ 计时面板与"到点提示音"都在 `CheckinTimerPanel` 里，本行**不再**自己
  //    调 `useServerTimer` / `useEstimateChime`（那正是"两份实现"的来源）。
  /** 「已超时 X」的秒数（`null` = 不显示）：判据只有 `@fht/shared` 一份。 */
  const overdueHint = homeworkOverdueSeconds(checkin);
  const isRunning = item.status === 'running';
  const isPaused = item.status === 'paused' || item.status === 'auto_paused';
  const isSubmitted = item.status === 'submitted';
  const isDone = item.status === 'done';
  const isSkipped = item.status === 'skipped';

  const interactive = canActOn(item);

  return (
    <li>
      <div
        className={cn(
          'rounded-2xl border bg-white',
          kid ? 'border-2 p-4' : 'border-slate-200 p-3',
          isRunning && 'border-brand-400 ring-2 ring-brand-100',
          // 「超时但仍能做」：琥珀色而不是红色 —— 红色像是在惩罚，而它其实还能做
          overdueHint !== null && !isDone && 'border-amber-300 bg-amber-50/40',
          isDone && 'bg-emerald-50/60',
          isSubmitted && 'border-amber-300 bg-amber-50/50',
          (item.retained || isSkipped) && 'opacity-60',
        )}
      >
        <div className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className={cn(
              'flex shrink-0 items-center justify-center rounded-2xl bg-brand-50',
              kid ? 'h-14 w-14 text-3xl' : 'h-11 w-11 text-xl',
            )}
          >
            {iconForName(item.name, isDone ? '✅' : '📘')}
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className={cn('font-semibold text-slate-900', kid ? 'text-xl' : 'text-base')}>
                {item.name}
              </p>
              <Badge tone={meta.tone}>
                {meta.icon} {meta.label}
              </Badge>
              {overdueHint !== null && (
                <Badge tone="warn">⏰ 已超时 {formatOverdueDuration(overdueHint)}</Badge>
              )}
              {item.retained && <Badge tone="neutral">已移出计划</Badge>}
            </div>

            <p className={cn('mt-1 text-slate-500', kid ? 'text-base' : 'text-xs')}>
              {[
                item.pointsPerUnit > 0 ? `完成可得 ${item.pointsPerUnit} 分` : '不记分',
                item.targetValue !== null ? `目标 ${item.targetValue}${item.unit ?? ''}` : null,
                item.checkin?.estimatedSeconds
                  ? `预计 ${formatPlannedDuration(item.checkin.estimatedSeconds)}`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>

            {/* ---------- 待家长验收：**必须显眼** ---------- */}
            {isSubmitted && (
              <div className="mt-2 rounded-xl bg-amber-50 px-3 py-2">
                <p className={cn('font-semibold text-amber-800', kid ? 'text-base' : 'text-sm')}>
                  📤 待家长验收 —— 这一项<strong>还没算完成、也没计分</strong>
                </p>
                <p className="mt-0.5 text-xs text-amber-700">
                  {item.pointsPerUnit > 0
                    ? `家长点「通过」后才会加 ${item.pointsPerUnit} 分；被打回就要重做。`
                    : '家长点「通过」后这一项才算完成；被打回就要重做。'}
                </p>
              </div>
            )}

            {/* ---------- 已完成 ---------- */}
            {isDone && (
              <p className={cn('mt-1 text-emerald-700', kid ? 'text-base' : 'text-xs')}>
                ✅ 家长已验收
                {/* 「用时」是**实际用时**（服务端 actualSeconds）→ 走 shared 的
                    formatDuration：45 秒就写"45 秒"，不再显示成"0 分钟" */}
                {item.checkin && item.checkin.actualSeconds > 0
                  ? ` · 用时 ${formatDuration(item.checkin.actualSeconds)}`
                  : ''}
                {item.checkin?.note ? ` · ${item.checkin.note}` : ''}
              </p>
            )}

            {/* ---------- 已放弃（家长点的，学生只读） ---------- */}
            {isSkipped && (
              <p className={cn('mt-1 text-slate-500', kid ? 'text-base' : 'text-xs')}>
                🚫 家长已放弃这一项，不用再做了
                {item.checkin?.skipReason ? ` · ${item.checkin.skipReason}` : ''}
              </p>
            )}

            {/* ---------- 计时中 / 暂停中：大号已用时间 ----------
                ⚠️ 这一块（以及"正在计时 / 已暂停"、心跳失败提示、超过两倍的提醒、
                到点提示音）**只有一个实现**：`components/ui/CheckinTimerPanel.tsx`。
                它由「今天的作业」/「超时」/「还有效」三条队列**共用** ——
                2026-10-17 用户实测的 bug 就是这块 JSX 当时抄了两份、
                第三处（队列行）没抄，于是孩子在那里看不到已用时间。 */}
            <CheckinTimerPanel
              checkinId={item.checkin?.checkinId ?? ''}
              status={item.status}
              actualSeconds={item.checkin?.actualSeconds ?? 0}
              estimatedSeconds={item.checkin?.estimatedSeconds ?? null}
              kid={kid}
              tone="brand"
              live
            />

            {/* 打回原因：家长 reject 时会写进 note，孩子要一眼看到"要改什么" */}
            {!isSubmitted && !isDone && !isSkipped && item.checkin?.note && (
              <p className="mt-1 text-xs text-rose-600">家长说：{item.checkin.note}</p>
            )}

            {/* 没有打卡记录：只读家长视角才会出现（学生自己打开时服务端会补齐实例） */}
            {item.checkin === null && (
              <p className="mt-1 text-xs text-slate-400">
                {isHomeworkItemEmpty(item)
                  ? '这一项还没有可操作的打卡记录，刷新一下就会出现。'
                  : '这一项当前没有可操作的打卡记录。'}
              </p>
            )}
          </div>
        </div>

        {/* ---------- 操作区 ----------
            ⛔ 刻意**没有**「放弃」：作业项的放弃只有家长能点
            （后端返回 HOMEWORK_SKIP_PARENT_ONLY）。界面上不暴露这个入口。 */}
        {interactive && (
          <div className={cn('mt-3 flex flex-wrap gap-2', kid && 'gap-3')}>
            {item.status === 'pending' && (
              <Button
                size={kid ? 'kid' : 'md'}
                variant={kid ? 'kid' : 'primary'}
                disabled={busy}
                icon={<span aria-hidden="true">▶️</span>}
                onClick={() => onStart(item)}
              >
                开始
              </Button>
            )}

            {isRunning && (
              <>
                <Button
                  size={kid ? 'kid' : 'md'}
                  variant="secondary"
                  disabled={busy}
                  icon={<span aria-hidden="true">⏸️</span>}
                  onClick={() => onPause(item)}
                >
                  暂停
                </Button>
                <Button
                  size={kid ? 'kid' : 'md'}
                  variant={kid ? 'kid-green' : 'primary'}
                  disabled={busy}
                  icon={<span aria-hidden="true">✅</span>}
                  onClick={() => onFinish(item)}
                >
                  完成
                </Button>
              </>
            )}

            {isPaused && (
              <>
                <Button
                  size={kid ? 'kid' : 'md'}
                  variant={kid ? 'kid' : 'primary'}
                  disabled={busy}
                  icon={<span aria-hidden="true">▶️</span>}
                  onClick={() => onResume(item)}
                >
                  继续
                </Button>
                <Button
                  size={kid ? 'kid' : 'md'}
                  variant={kid ? 'kid-green' : 'primary'}
                  disabled={busy}
                  onClick={() => onFinish(item)}
                >
                  完成
                </Button>
              </>
            )}

            {isSubmitted && (
              <p className="text-xs text-amber-700">
                已经交给家长了，等家长点「通过」就好 —— 不用再点一次。
              </p>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

// ================================================================ 超时条目
//
// ⚠️ 超时行的**呈现与判据不在这里**：它是与家长端**共用的同一个组件**
// `components/homework/OverdueRow.tsx`（用户要求两端口径一致：同样的排序、
// 同样的"已超时 X"、同样说明"仍然可以做"）。
// 这个页面**刻意不传 `onSkip`** —— 「放弃」是家长的决定，学生端连按钮都不渲染
// （后端也会返回 `HOMEWORK_SKIP_PARENT_ONLY`，但我们不靠"点了会失败"来保护）。

// ================================================================ 页面入口

/**
 * 入口包装：把 **auth_user.id 翻译成 student.id**（见 `lib/useMyStudent.ts`）。
 *
 * 与今日打卡页**完全同一套写法**：解析成功之前不发任何业务请求（避免 404）。
 */
export function StudentHomeworkPage() {
  const my = useMyStudent();

  if (my.isLoading) {
    return (
      <AppShell kind="student" title="我的作业">
        <div className="space-y-3">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </AppShell>
    );
  }

  if (!my.studentId) {
    return (
      <AppShell kind="student" title="我的作业">
        <ErrorState
          message={toErrorMessage(my.error) || '没能确认你的学生档案，请稍后重试'}
          onRetry={my.refetch}
        />
      </AppShell>
    );
  }

  return <StudentHomeworkScreen studentId={my.studentId} />;
}

function StudentHomeworkScreen({
  studentId,
}: {
  /** **student.id**（学生档案主键），不是 auth_user.id。 */
  studentId: string;
}) {
  const toast = useToast();
  const { user } = useAuth();

  const [tab, setTab] = useState<HomeworkTab>('today');
  const dayQuery = useHomeworkDay(studentId, null);
  const overdueQuery = useHomeworkOverdue(studentId);
  /**
   * 「**还有效**」队列（还没到期、但已经不是今天布置的未完成项）。
   *
   * ⚠️ 这一条是 2026-10-17 用户报的那个 bug 的修复点：没有它，周五布置、
   * 周日截止的两项作业在周六**既不在「今天的作业」也不在「超时」**里，
   * 孩子打开作业页看到的是"今天没有布置作业"，连「完成」按钮都点不到。
   */
  const openQuery = useHomeworkOpen(studentId);
  /**
   * 积分只用于头部那颗 ⭐。作业接口本身**不返回**余额（今日打卡页才有），
   * 所以这里单独取一次 —— 它同时提供 `useHomeworkCheckinMutations` 的失效目标，
   * 做完一项后 ⭐ 会跟着刷新。
   */
  const pointsQuery = usePointsSummary(studentId);
  const mutations = useHomeworkCheckinMutations(studentId);

  /**
   * 儿童模式：与今日打卡页同一套判定口径（`resolveUiMode`）。
   *
   * ⚠️ 作业接口**不返回** `uiMode`（今日打卡接口才返回），所以这里只有 `serverUiMode`
   *    这一个来源可为空 —— 空值一律落到标准模式。
   */
  const mode = resolveUiMode({ serverUiMode: null });
  const kid = isChildMode(mode);

  const [durationTarget, setDurationTarget] = useState<{ id: string; name: string } | null>(null);
  const [durationInput, setDurationInput] = useState('20');
  /** 这次弹窗预填值的来源（家长目标 / 上次预计 / 兜底），见 shared 的 `start-estimate.ts`。 */
  const [durationEstimate, setDurationEstimate] = useState<StartEstimate | null>(null);
  const [finishTarget, setFinishTarget] = useState<{
    id: string;
    name: string;
    /** 作业项的目标量（`null` = 没设目标 → 完成量可选）。判定见 `lib/finishAmount.ts`。 */
    targetValue: number | null;
    unit: string | null;
  } | null>(null);
  const [finishValue, setFinishValue] = useState('');
  const [finishNote, setFinishNote] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const day = dayQuery.data;
  const subjects = useMemo(() => (day?.subjects ?? []).filter((subject) => !subject.retained), [day]);
  const overdue = overdueQuery.data?.entries ?? [];
  /**
   * 「还有效」的条目（服务端已按 `dueAt` **正序**排好，前端不重排）。
   *
   * ⚠️ 去重按 `id`（= checkinId）而不是按名字：同名作业项可以是**两项**真实的作业
   * （用户报的那次就是 2 项），按名字去重会吞掉孩子真要做的一项。
   * 判据与实现在 `@fht/shared` 的 `dedupeHomeworkQueueEntries()`（唯一一份）。
   */
  const openEntries = useMemo(
    () => dedupeHomeworkQueueEntries(openQuery.data?.entries ?? []),
    [openQuery.data],
  );
  /**
   * 今天这一栏有没有可看的作业（没有作业单、或只剩"已移除"的科目）。
   *
   * 为什么孩子端需要它：用户报的那个场景里今天**根本没有作业单**
   * （周五布置的那张属于周五），于是「今天的作业」是一片"今天没有布置作业"，
   * 而真正要做的那两项在「还有效」里 —— 不给出入口，孩子会以为作业没了。
   */
  const todayHasHomework = Boolean(day?.exists) && subjects.length > 0;

  const busy =
    mutations.start.isPending ||
    mutations.pause.isPending ||
    mutations.resume.isPending ||
    mutations.finish.isPending;

  // ---------------------------------------------------------------- 动作

  /** 开始计时：必须先填预计时长（服务端要求 `estimatedSeconds`，与今日打卡同一路径）。 */
  const confirmStart = async () => {
    if (!durationTarget) return;
    /**
     * 借这次**真实的点击手势**解锁音频（理由同今日打卡页：二十分钟后那一声
     * 不在手势里，iOS 会静默拒绝）。见 `lib/estimateChimeAudio.ts`。
     */
    primeEstimateChimeAudio();
    const minutes = Number(durationInput);
    if (!Number.isFinite(minutes) || minutes < 1) {
      setActionError('预计时长至少 1 分钟');
      return;
    }
    setActionError(null);
    try {
      await mutations.start.mutateAsync({
        checkinId: durationTarget.id,
        estimatedSeconds: Math.round(minutes * 60),
        // 一次用户意图一个幂等键：双击「开始」不会产生两条记录
        idempotencyKey: newIdempotencyKey(`start:${durationTarget.id}`),
      });
      toast.showToast('开始计时，加油！', 'success');
      setDurationTarget(null);
    } catch (err) {
      setActionError(toErrorMessage(err));
    }
  };

  const doPause = async (id: string) => {
    try {
      await mutations.pause.mutateAsync({
        checkinId: id,
        idempotencyKey: newIdempotencyKey(`pause:${id}`),
      });
    } catch (err) {
      toast.showToast(toErrorMessage(err), 'error');
    }
  };

  const doResume = async (id: string) => {
    try {
      await mutations.resume.mutateAsync({
        checkinId: id,
        idempotencyKey: newIdempotencyKey(`resume:${id}`),
      });
    } catch (err) {
      toast.showToast(toErrorMessage(err), 'error');
    }
  };

  /**
   * 打开「完成」弹窗（交给家长验收）。
   *
   * ⚠️ **不预填**实际完成量（这里曾经是 `targetValue !== null ? String(targetValue) : ''`）：
   *   预填 = 孩子顺手一点就替他填了"我做满了目标"，而这个数字会算分、进统计。
   *   现在一律从空串开始；有目标值时必填，判定见 `lib/finishAmount.ts`。
   */
  const openFinish = (
    id: string,
    name: string,
    targetValue: number | null,
    unit: string | null,
  ) => {
    setFinishTarget({ id, name, targetValue, unit });
    setFinishValue('');
    setFinishNote('');
    setActionError(null);
  };

  /**
   * 完成。作业项的 `need_approval = true`，所以服务端会把它置成 `submitted`
   * （**不计分**）—— 界面上必须说成"待家长验收"，不能提示"完成啦"。
   */
  const confirmFinish = async () => {
    if (!finishTarget) return;
    /** 唯一的校验入口：有目标值就必须自己填；没目标值可以留空（= 完成一次）。 */
    const check = checkFinishAmount(finishValue, finishTarget.targetValue);
    if (!check.ok) {
      setActionError(check.error);
      return;
    }
    setActionError(null);
    try {
      await mutations.finish.mutateAsync({
        checkinId: finishTarget.id,
        valueDone: check.valueDone,
        note: finishNote.trim(),
        idempotencyKey: newIdempotencyKey(`finish:${finishTarget.id}`),
      });
      toast.showToast('这一项交给家长验收了，通过后才会加分', 'success');
      setFinishTarget(null);
    } catch (err) {
      setActionError(toErrorMessage(err));
    }
  };

  /**
   * 打开「预计时长」弹窗。
   *
   * 预填值：**家长设的目标时间 > 上一次的预计 > 20 分钟**
   * （判定与单位换算在 `@fht/shared` 的 `start-estimate.ts`，与「今日打卡」同一份）。
   * 家长把作业设成目标 40 分钟时，孩子点开始就该看到 40，而不是 20。
   */
  const openDuration = (target: {
    id: string;
    name: string;
    targetValue?: number | null;
    unit?: string | null;
    estimatedSeconds?: number | null;
  }) => {
    setDurationTarget({ id: target.id, name: target.name });
    const estimate = defaultStartEstimate({
      targetValue: target.targetValue,
      unit: target.unit,
      estimatedSeconds: target.estimatedSeconds,
    });
    setDurationEstimate(estimate);
    setDurationInput(String(estimate.minutes));
    setActionError(null);
  };

  const startFromItem = (item: HomeworkItemView) => {
    openDuration({
      id: item.checkin?.checkinId ?? '',
      name: item.name,
      targetValue: item.targetValue,
      unit: item.unit,
      estimatedSeconds: item.checkin?.estimatedSeconds,
    });
  };

  const summary = day?.summary;

  return (
    <AppShell
      kind="student"
      title={kid ? '今天的作业' : '我的作业'}
      compactHeader={kid}
      /* 返回箭头由 `lib/backNav.ts` 统一给出（学生端都在底部 tab 里 → 没有箭头），
         页面不再自己传 `backTo`。 */
      headerAction={
        <span
          className={cn(
            'rounded-xl bg-amber-50 font-bold text-amber-700',
            kid ? 'px-3 py-2 text-lg' : 'px-3 py-1.5 text-sm',
          )}
          aria-label={`当前积分 ${pointsQuery.data?.balance ?? 0}`}
        >
          ⭐ {pointsQuery.data?.balance ?? 0}
        </span>
      }
    >
      <div className={cn('space-y-5', kid && 'ui-child space-y-6')}>
        {/* 首登强制改密提醒（与今日打卡页同一处提醒，学生未必会点"设置"） */}
        {user?.mustChangePassword && (
          <Link
            to="/student/settings"
            className={cn(
              'block rounded-2xl bg-amber-50 text-amber-800 hover:bg-amber-100',
              kid ? 'px-4 py-4 text-lg' : 'px-4 py-3 text-sm',
            )}
          >
            你现在用的是<strong>家长给的初始密码</strong>，点这里改成只有你自己知道的密码 →
          </Link>
        )}

        {/* ---------- 进度 ---------- */}
        <section
          aria-label="作业进度"
          className={cn(
            'rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 text-white',
            kid ? 'p-5' : 'p-4',
          )}
        >
          <p className={cn('text-brand-50', kid ? 'text-lg' : 'text-sm')}>今天的作业完成</p>
          <p className={cn('font-bold tabular-nums', kid ? 'text-5xl' : 'text-3xl')}>
            {summary?.doneCount ?? 0}
            <span className={cn('ml-1 font-medium', kid ? 'text-2xl' : 'text-lg')}>
              / {summary?.itemCount ?? 0} 项
            </span>
          </p>
          <p className={cn('mt-2 text-brand-50', kid ? 'text-base' : 'text-xs')}>
            {!day?.exists
              ? '今天家长还没有布置作业'
              : (summary?.submittedCount ?? 0) > 0
                ? `有 ${summary?.submittedCount} 项在等家长验收，验收通过才算完成`
                : summary?.allCompleted
                  ? '今天的作业全部完成，太棒了！'
                  : `每科下面的作业都完成，这一科才算完成（已完成 ${summary?.completedSubjectCount ?? 0}/${summary?.subjectCount ?? 0} 科）`}
          </p>
        </section>

        {/* ---------- 三个标签：今天的作业 / 还有效 / 超时 ----------
            ⚠️ 「还有效」是 2026-10-17 新增的那一格：还没到期、但**不是今天布置**的
            未完成项。用户报的 bug 就是它没有归属 —— 周五布置、周日截止的作业，
            周六在「今天的作业」（按布置日筛）与「超时」（按截止时刻筛）里都看不见。
            ⚠️ 儿童模式下标签文字用短的（`今天 / 还有效 / 超时`）：三个 `flex-1` 的
            按钮在 320px 上放不下"今天的作业"这五个字，会挤成两行。 */}
        <div role="tablist" aria-label="作业视图" className="flex gap-2">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'today'}
            onClick={() => setTab('today')}
            className={cn(
              'rounded-xl font-semibold transition-colors',
              kid ? 'min-h-[56px] flex-1 text-base' : 'flex-1 px-3 py-2 text-sm',
              tab === 'today'
                ? 'bg-brand-600 text-white'
                : 'bg-white text-slate-600 ring-1 ring-slate-200',
            )}
          >
            📚 {kid ? '今天' : '今天的作业'}
            {summary && summary.itemCount > 0 && ` ${summary.doneCount}/${summary.itemCount}`}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'open'}
            onClick={() => setTab('open')}
            className={cn(
              'rounded-xl font-semibold transition-colors',
              kid ? 'min-h-[56px] flex-1 text-base' : 'flex-1 px-3 py-2 text-sm',
              tab === 'open'
                ? 'bg-sky-600 text-white'
                : 'bg-white text-slate-600 ring-1 ring-slate-200',
            )}
          >
            📌 还有效
            {openEntries.length > 0 && ` ${openEntries.length}`}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'overdue'}
            onClick={() => setTab('overdue')}
            className={cn(
              'rounded-xl font-semibold transition-colors',
              kid ? 'min-h-[56px] flex-1 text-base' : 'flex-1 px-3 py-2 text-sm',
              tab === 'overdue'
                ? 'bg-amber-500 text-white'
                : 'bg-white text-slate-600 ring-1 ring-slate-200',
            )}
          >
            ⏰ 超时
            {overdue.length > 0 && ` ${overdue.length}`}
          </button>
        </div>

        {/* ---------- 「今天没有作业」但「还有效」里有东西时的入口 ----------
            用户报的那一幕：今天（周六）根本没有作业单，这一栏写着"今天没有布置作业"，
            而真正要做的那两项在「还有效」里。不给这个入口，孩子会以为作业没了。 */}
        {tab === 'today' && !todayHasHomework && openEntries.length > 0 && (
          <button
            type="button"
            onClick={() => setTab('open')}
            className={cn(
              'block w-full rounded-2xl border-2 border-sky-300 bg-sky-50 text-left text-sky-900',
              kid ? 'p-4 text-lg' : 'p-3 text-sm',
            )}
          >
            <strong>
              📌 还有 {openEntries.length} 项作业没到期，现在就能做 →
            </strong>
            <span className={cn('mt-1 block text-sky-700', kid ? 'text-base' : 'text-xs')}>
              这些作业不是今天布置的，但也还没超时，所以不在这一栏里。
            </span>
          </button>
        )}

        {/* ---------- 今天的作业 ---------- */}
        {tab === 'today' &&
          (dayQuery.isLoading ? (
            <div className="space-y-3">
              <Skeleton className={kid ? 'h-24 w-full' : 'h-16 w-full'} />
              <Skeleton className={kid ? 'h-24 w-full' : 'h-16 w-full'} />
            </div>
          ) : dayQuery.isError ? (
            <ErrorState
              message={toErrorMessage(dayQuery.error)}
              onRetry={() => void dayQuery.refetch()}
            />
          ) : !day?.exists || subjects.length === 0 ? (
            <EmptyState
              icon="🌱"
              title="今天没有布置作业"
              description="家长还没录今天的作业。没有作业的时候，就去玩一会儿吧。"
            />
          ) : (
            <section aria-labelledby="homework-today-heading" className="space-y-4">
              <h2 id="homework-today-heading" className="sr-only">
                今天的作业（按科目分组）
              </h2>
              {subjects.map((subject) => {
                const submitted = subject.items.filter(
                  (item) => item.status === 'submitted',
                ).length;
                return (
                  <Card
                    key={subject.nodeId}
                    className={cn(subject.completion.completed && 'border-emerald-200 bg-emerald-50/40')}
                  >
                    {/* 科目头：一眼看出"这一科算不算完成" */}
                    <div className="flex flex-wrap items-center gap-2">
                      <span aria-hidden="true" className={cn(kid ? 'text-2xl' : 'text-xl')}>
                        {iconForName(subject.name, '📘')}
                      </span>
                      <h3
                        className={cn(
                          'min-w-0 flex-1 truncate font-semibold text-slate-900',
                          kid ? 'text-xl' : 'text-base',
                        )}
                      >
                        {subject.name}
                      </h3>
                      {subject.completion.completed ? (
                        <Badge tone="success">✅ 这一科完成</Badge>
                      ) : (
                        <Badge tone="neutral">
                          已完成 {subject.completion.done}/{subject.completion.total}
                          {subject.completion.abandoned > 0
                            ? `（${subject.completion.abandoned} 项已放弃）`
                            : ''}
                        </Badge>
                      )}
                      {submitted > 0 && <Badge tone="warn">📤 待验收 {submitted}</Badge>}
                    </div>

                    {/*
                      📅 这一科**到哪天为止**（2026-10-16 新增）。

                      为什么孩子端也要说：周五布置的作业现在默认留到周日，
                      而这一页原来**一个字都没提截止日期** —— 孩子周六打开只看到"待完成"，
                      既不知道还来得及，也不知道什么时候算迟。
                      措辞刻意是"做完就行"（`describeStudentHomeworkDueText()` 里那一份，
                      **不含任何催促性词**：没有"还剩""尽快""别拖"）：这一页是给孩子看的，
                      知道期限就够了，压力不该由界面来加。
                    */}
                    <p className={cn('mt-2 text-slate-500', kid ? 'text-base' : 'text-xs')}>
                      📅{' '}
                      {describeStudentHomeworkDueText({
                        planDate: day.date,
                        dueDate: subject.dueDate,
                        dueTimeSource: subject.dueTimeSource,
                        dueTime: subject.dueTime,
                        durationMinutes: subject.durationMinutes,
                      })}
                    </p>

                    <ul className="mt-3 space-y-3">
                      {subject.items.map((item) => (
                        <HomeworkItemRow
                          key={item.habitId}
                          item={item}
                          kid={kid}
                          busy={busy}
                          checkin={item.checkin}
                          onStart={startFromItem}
                          onPause={(target) => void doPause(target.checkin?.checkinId ?? '')}
                          onResume={(target) => void doResume(target.checkin?.checkinId ?? '')}
                          onFinish={(target) =>
                            openFinish(
                              target.checkin?.checkinId ?? '',
                              target.name,
                              target.targetValue,
                              target.unit,
                            )
                          }
                        />
                      ))}
                    </ul>
                  </Card>
                );
              })}
            </section>
          ))}

        {/* ---------- 「还有效」（还没到期，但不是今天布置的） ----------
            ⚠️ 与「超时」共用**同一个** `OverdueRow` 组件，只换 `queue`：
            两条队列的动作（开始/暂停/完成/**计时显示**）完全一样，另写一份必然漂移。
            ⚠️ `live` 是"这一端就是计时的那一端"：学生端传 true（数字会走、会发心跳、
            到点会响）。家长端**不传** —— 那边只读，心跳会被 403。
            ⚠️ 排序（`dueAt` 正序）由服务端保证，前端**不重排**。 */}
        {tab === 'open' &&
          (openQuery.isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : openQuery.isError ? (
            <ErrorState
              message={toErrorMessage(openQuery.error)}
              onRetry={() => void openQuery.refetch()}
            />
          ) : openEntries.length === 0 ? (
            <EmptyState
              icon="🌤️"
              title="没有还没到期的作业"
              description="昨天、前天布置但还没到期的作业会留在这里，直到你做完、或者家长点「放弃」。现在这里是空的。"
            />
          ) : (
            <section aria-labelledby="homework-open-heading" className="space-y-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2
                  id="homework-open-heading"
                  className={cn(
                    'font-semibold text-slate-700',
                    kid ? 'text-xl' : 'text-sm uppercase tracking-wide text-slate-500',
                  )}
                >
                  {kid ? '还没到期，现在就能做' : '还有效（还没到期）'}
                </h2>
                <span className="text-xs text-slate-400">
                  按截止时间排序，越快到期越靠前
                </span>
              </div>
              <p className="rounded-xl bg-sky-50 px-3 py-2 text-xs text-sky-900">
                这些作业<strong>还没到期</strong>，而且不是今天布置的 ——
                现在就能做：做完点「完成」交给家长验收，它才会从这里消失。
                家长也可以点「放弃」把某一项收掉。
              </p>
              <ul className="space-y-3">
                {openEntries.map((entry) => (
                  <OverdueRow
                    key={entry.id}
                    entry={entry}
                    queue="open"
                    openBadge={entry.badge}
                    kid={kid}
                    busy={busy}
                    live
                    onStart={() => {
                      openDuration({
                        id: entry.id,
                        name: entry.habitName,
                        targetValue: entry.targetValue,
                        unit: entry.unit,
                        estimatedSeconds: entry.estimatedSeconds,
                      });
                    }}
                    onPause={() => void doPause(entry.id)}
                    onResume={() => void doResume(entry.id)}
                    /* ⚠️ 必须把「目标量 / 单位」一起传进去：与「今天的作业」的行
                       用**同一个**完成弹窗与同一条校验（`lib/finishAmount.ts`）。
                       以前这里写死 `null, null`，于是从「还有效」里点「完成」时
                       目标量那一条校验被绕过 —— 孩子漏填就变成"没做满"却不报错。 */
                    onFinish={() =>
                      openFinish(entry.id, entry.habitName, entry.targetValue ?? null, entry.unit ?? null)
                    }
                  />
                ))}
              </ul>
            </section>
          ))}

        {/* ---------- 超时（与"今天的作业"分开的标签） ---------- */}
        {tab === 'overdue' &&
          (overdueQuery.isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : overdueQuery.isError ? (
            <ErrorState
              message={toErrorMessage(overdueQuery.error)}
              onRetry={() => void overdueQuery.refetch()}
            />
          ) : overdue.length === 0 ? (
            <EmptyState
              icon="🎉"
              title="没有超时的作业"
              description="过期的作业会一直留在这里，直到你做完、或者家长点「放弃」。现在这里是空的，说明没有欠账。"
            />
          ) : (
            <section aria-labelledby="homework-overdue-heading" className="space-y-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2
                  id="homework-overdue-heading"
                  className={cn(
                    'font-semibold text-slate-700',
                    kid ? 'text-xl' : 'text-sm uppercase tracking-wide text-slate-500',
                  )}
                >
                  {kid ? '还没做完的（超时了）' : '超时未完成'}
                </h2>
                <span className="text-xs text-slate-400">
                  按截止时间排序，最新的在最上面
                </span>
              </div>
              <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">
                这些作业已经过了截止时间，但<strong>仍然可以做</strong> ——
                做完点「完成」交给家长验收，它才会从这里消失。
                家长也可以点「放弃」把某一项收掉。
              </p>
              <ul className="space-y-3">
                {overdue.map((entry) => (
                  <OverdueRow
                    key={entry.id}
                    entry={entry}
                    kid={kid}
                    busy={busy}
                    live
                    onStart={() => {
                      openDuration({
                        id: entry.id,
                        name: entry.habitName,
                        targetValue: entry.targetValue,
                        unit: entry.unit,
                        estimatedSeconds: entry.estimatedSeconds,
                      });
                    }}
                    onPause={() => void doPause(entry.id)}
                    onResume={() => void doResume(entry.id)}
                    /* 同上：「目标量 / 单位」必须一起传，否则完成量校验在超时这一栏失效 */
                    onFinish={() =>
                      openFinish(entry.id, entry.habitName, entry.targetValue ?? null, entry.unit ?? null)
                    }
                  />
                ))}
              </ul>
            </section>
          ))}

        {/* ---------- 说明（儿童模式隐藏，避免干扰） ---------- */}
        {!kid && (
          <Card>
            <ul className="list-inside list-disc space-y-1 text-sm text-slate-500">
              <li>点「开始」先填预计时长，然后开始计时；计时中可以暂停、继续。</li>
              <li>
                已用时间由<strong>服务端时间戳</strong>累计，改手机的时间不会影响统计。
              </li>
              <li>
                作业点「完成」后是<strong>待家长验收</strong>：家长点「通过」才计分，
                被打回就要重做。
              </li>
              <li>「放弃」只能由家长来点 —— 作业不做了是家长的决定。</li>
              <li>{uiModeHint(mode)}</li>
            </ul>
          </Card>
        )}
      </div>

      {/* ---------- 第 1 步：填预计时长 ---------- */}
      <Modal
        open={durationTarget !== null}
        onClose={() => setDurationTarget(null)}
        title="预计要做多久？"
        description={durationTarget?.name}
        size="sm"
        footer={
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:gap-3">
            <Button
              variant="secondary"
              size={kid ? 'kid' : 'md'}
              className="sm:flex-1"
              onClick={() => setDurationTarget(null)}
            >
              再想想
            </Button>
            <Button
              size={kid ? 'kid' : 'md'}
              variant={kid ? 'kid-green' : 'primary'}
              className="sm:flex-1"
              loading={mutations.start.isPending}
              onClick={() => void confirmStart()}
            >
              开始计时
            </Button>
          </div>
        }
      >
        <div className={cn('space-y-3', kid && 'ui-child')}>
          {durationEstimate?.source === 'parent-target' && durationEstimate.targetMinutes !== null && (
            <p className="rounded-xl bg-brand-50 px-3 py-2 text-sm text-brand-800">
              家长设的目标是 <strong>{durationEstimate.targetMinutes} 分钟</strong>，已经帮你填好了。
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {startEstimatePresets(durationEstimate?.targetMinutes ?? null, DURATION_PRESETS).map((minutes) => (
              <button
                key={minutes}
                type="button"
                aria-pressed={durationInput === String(minutes)}
                onClick={() => setDurationInput(String(minutes))}
                className={cn(
                  'rounded-xl font-semibold transition-colors',
                  kid ? 'min-h-[56px] px-5 text-xl' : 'px-3 py-2 text-sm',
                  durationInput === String(minutes)
                    ? 'bg-brand-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200',
                )}
              >
                {minutes} 分钟
              </button>
            ))}
          </div>
          <Input
            label="或者直接填分钟数"
            type="number"
            min={1}
            max={720}
            kid={kid}
            value={durationInput}
            onChange={(e) => setDurationInput(e.target.value)}
            hint="最少 1 分钟，最多 12 小时"
          />
          {actionError && (
            <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {actionError}
            </p>
          )}
        </div>
      </Modal>

      {/* ---------- 第 2 步：完成 → 待家长验收 ---------- */}
      <Modal
        open={finishTarget !== null}
        onClose={() => setFinishTarget(null)}
        title="这一项做完了？"
        description={finishTarget?.name}
        size="sm"
        footer={
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:gap-3">
            <Button
              variant="secondary"
              size={kid ? 'kid' : 'md'}
              className="sm:flex-1"
              onClick={() => setFinishTarget(null)}
            >
              还没好
            </Button>
            <Button
              size={kid ? 'kid' : 'md'}
              variant={kid ? 'kid-green' : 'primary'}
              className="sm:flex-1"
              loading={mutations.finish.isPending}
              onClick={() => void confirmFinish()}
            >
              交给家长验收
            </Button>
          </div>
        }
      >
        {finishTarget && (
          <div className={cn('space-y-3', kid && 'ui-child')}>
            {/* 把"点完不等于拿到分"这件事说在最前面 —— 这是本轮明确要求的一条 */}
            <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800">
              📤 交上去之后是<strong>待家长验收</strong>：家长点「通过」这一项才算完成、才会加分；
              如果家长打回，就要改好再交一次。
            </p>

            {/* 「实际完成量」——**永远从空开始**，有目标值时必须自己填（校验见 confirmFinish）。
                提示走 `hint` 常驻在输入框下面；点提交没填时下面的红条会明确说出来。 */}
            <Input
              label={finishAmountLabel(finishTarget.targetValue, finishTarget.unit)}
              type="number"
              min={1}
              inputMode="decimal"
              kid={kid}
              value={finishValue}
              onChange={(e) => setFinishValue(e.target.value)}
              hint={finishAmountHint(finishTarget.targetValue)}
            />

            <Input
              label="想对家长说的话"
              kid={kid}
              maxLength={500}
              value={finishNote}
              onChange={(e) => setFinishNote(e.target.value)}
              placeholder="例如：这一课的生字抄了三遍"
              hint="不想写就留空。"
            />

            {actionError && (
              <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {actionError}
              </p>
            )}
          </div>
        )}
      </Modal>

      {/* ⚠️ 刻意**没有**「放弃」弹窗：作业项的放弃只有家长能点，
          学生端界面不暴露这个入口（后端会返回 HOMEWORK_SKIP_PARENT_ONLY）。 */}
    </AppShell>
  );
}
